import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Ensure the free-shipping UI is correct whenever this drawer is in the DOM
    CartDrawerShippingSync.ensureInitialized();
    CartDrawerShippingSync.refreshFromServer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // Always sync shipping message after add events (covers themes that don't auto-refresh sections)
    CartDrawerShippingSync.ensureInitialized();
    CartDrawerShippingSync.refreshFromServer();
  };

  open() {
    this.showDialog();

    // Sync shipping message as soon as the drawer opens
    CartDrawerShippingSync.ensureInitialized();
    CartDrawerShippingSync.refreshFromServer();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * -------------------------------
 * FREE SHIPPING BAR (RACE-SAFE)
 * -------------------------------
 *
 * The core problem you described ("wrong at times then fixes itself") is almost always
 * caused by out-of-order AJAX updates (race condition) when multiple cart requests fire.
 *
 * This file solves it by:
 *  - watching ALL cart mutation requests (fetch + XHR)
 *  - keeping a monotonically increasing sequence counter
 *  - pulling the *final truth* from /cart.js and updating the banner from that
 *  - ignoring stale responses
 */

/**
 * Money formatting fallback (in case Shopify.formatMoney or settings.money_format isn't available)
 */
function getMoneyFormat() {
  // Common theme locations:
  // - window.settings.money_format
  // - window.theme.money_format
  // - window.Shopify.money_format
  // - <meta name="shopify-money-format" content="...">
  const fromWindow =
    (window.settings && window.settings.money_format) ||
    (window.theme && window.theme.money_format) ||
    (window.Shopify && window.Shopify.money_format);

  if (fromWindow) return fromWindow;

  const meta = document.querySelector('meta[name="shopify-money-format"]')?.content;
  if (meta) return meta;

  return '${{amount}}';
}

function formatMoneyFallback(cents, format) {
  // Very small compatible formatter for common placeholders.
  // Supports: amount, amount_no_decimals, amount_with_comma_separator, amount_no_decimals_with_comma_separator
  const value = typeof cents === 'number' ? cents : Number(cents || 0);
  const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
  const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

  const amount = value / 100;

  const formatWithDelimiters = (num, precision, thousands, decimal) => {
    const fixed = num.toFixed(precision);
    const parts = fixed.split('.');
    const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const fraction = parts[1] ? decimal + parts[1] : '';
    return integer + fraction;
  };

  let formatted = '';
  switch (placeholder) {
    case 'amount_no_decimals':
      formatted = formatWithDelimiters(amount, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      formatted = formatWithDelimiters(amount, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      formatted = formatWithDelimiters(amount, 0, '.', ',');
      break;
    case 'amount':
    default:
      formatted = formatWithDelimiters(amount, 2, ',', '.');
      break;
  }

  return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
}

function formatMoney(cents) {
  const format = getMoneyFormat();

  // Prefer Shopify.formatMoney if present
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    try {
      return window.Shopify.formatMoney(cents, format);
    } catch (e) {
      // fall through
    }
  }

  return formatMoneyFallback(cents, format);
}

/**
 * Updates the free shipping UI from a cart object (from /cart.js).
 *
 * Supports TWO markup patterns:
 *  1) Your current JS markup:
 *     - #shipping-bar-container[data-threshold]
 *     - .js-shipping-progress-bar
 *     - .js-shipping-text-wrapper
 *
 *  2) The data-hook markup pattern (optional):
 *     - .cart-drawer__shipping-banner[data-free-shipping-threshold-cents]
 *     - [data-shipping-progress]
 *     - [data-shipping-text]
 */
function updateFreeShippingBar(cart) {
  const cartTotal = Number(cart?.total_price || 0);

  // --- Pattern 1: shipping-bar-container ---
  const container = document.getElementById('shipping-bar-container');
  if (container) {
    const threshold = Number.parseInt(container.dataset.threshold, 10) || 0;
    if (!threshold) return;

    const remaining = Math.max(0, threshold - cartTotal);
    const percentage = Math.min((cartTotal / threshold) * 100, 100);

    const progressBar = container.querySelector('.js-shipping-progress-bar');
    const messageWrapper = container.querySelector('.js-shipping-text-wrapper');

    if (progressBar) progressBar.style.width = `${percentage}%`;

    if (messageWrapper) {
      if (remaining > 0) {
        const formattedRemaining = formatMoney(remaining);
        messageWrapper.classList.remove('cart-drawer__shipping-text--success');
        messageWrapper.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
          <span>Add <strong>${formattedRemaining}</strong> more for <strong>FREE shipping!</strong></span>
        `;
      } else {
        messageWrapper.classList.add('cart-drawer__shipping-text--success');
        messageWrapper.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
          <span>Congrats! You've unlocked <strong>FREE shipping!</strong></span>
        `;
      }
    }

    return; // done for Pattern 1
  }

  // --- Pattern 2: cart-drawer__shipping-banner with hooks ---
  const banner = document.querySelector('.cart-drawer__shipping-banner');
  if (!banner) return;

  const threshold =
    Number(banner.dataset.freeShippingThresholdCents) ||
    Number.parseInt(banner.getAttribute('data-threshold') || '0', 10) ||
    0;

  if (!threshold) return;

  const remaining = Math.max(0, threshold - cartTotal);
  const percentage = Math.min((cartTotal / threshold) * 100, 100);

  const progressBar =
    banner.querySelector('[data-shipping-progress]') ||
    banner.querySelector('.cart-drawer__shipping-progress-bar');

  const messageWrapper =
    banner.querySelector('[data-shipping-text]') ||
    banner.querySelector('.cart-drawer__shipping-text');

  if (progressBar) progressBar.style.width = `${percentage}%`;

  if (messageWrapper) {
    if (remaining > 0) {
      const formattedRemaining = formatMoney(remaining);
      messageWrapper.classList.remove('cart-drawer__shipping-text--success');
      messageWrapper.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
        <span>Add <strong>${formattedRemaining}</strong> more for <strong>FREE shipping!</strong></span>
      `;
    } else {
      messageWrapper.classList.add('cart-drawer__shipping-text--success');
      messageWrapper.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        <span>Congrats! You've unlocked <strong>FREE shipping!</strong></span>
      `;
    }
  }
}

/**
 * Singleton that keeps the free-shipping bar correct even under rapid cart changes.
 */
const CartDrawerShippingSync = (() => {
  let initialized = false;
  let seq = 0;

  // Avoid feedback loops: our own /cart.js fetch should not trigger another refresh
  let internalCartFetchInFlight = false;

  const CART_MUTATION_ENDPOINTS = [
    '/cart/add',
    '/cart/change',
    '/cart/update',
    '/cart/clear',
    '/checkout', // sometimes triggered by dynamic checkout flows; harmless
  ];

  function isCartMutationUrl(urlString) {
    if (!urlString) return false;
    try {
      const url = new URL(urlString, window.location.origin);
      const path = url.pathname || '';
      return CART_MUTATION_ENDPOINTS.some((p) => path.startsWith(p));
    } catch {
      // If URL parsing fails, fall back to substring checks
      return CART_MUTATION_ENDPOINTS.some((p) => String(urlString).includes(p));
    }
  }

  async function fetchCartJsonForSeq(localSeq) {
    // If a newer update exists, stop early
    if (localSeq !== seq) return null;

    internalCartFetchInFlight = true;
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (!res.ok) return null;
      const cart = await res.json();

      // Ignore stale results
      if (localSeq !== seq) return null;

      return cart;
    } catch {
      return null;
    } finally {
      internalCartFetchInFlight = false;
    }
  }

  async function refreshFromServer() {
    const localSeq = ++seq;

    const cart = await fetchCartJsonForSeq(localSeq);
    if (!cart) return;

    updateFreeShippingBar(cart);
  }

  function patchFetch() {
    if (!window.fetch || window.fetch.__cartDrawerPatched) return;

    const originalFetch = window.fetch.bind(window);

    const patchedFetch = async (...args) => {
      const req = args[0];
      const url =
        typeof req === 'string'
          ? req
          : req && typeof req === 'object' && 'url' in req
            ? req.url
            : '';

      const shouldWatch = !internalCartFetchInFlight && isCartMutationUrl(url);

      const response = await originalFetch(...args);

      // Trigger refresh after mutation request resolves (race-safe via seq)
      if (shouldWatch) {
        // Don't await (avoid blocking UI), but keep order safety via seq
        refreshFromServer();
      }

      return response;
    };

    patchedFetch.__cartDrawerPatched = true;
    window.fetch = patchedFetch;
    window.fetch.__cartDrawerPatched = true;
  }

  function patchXHR() {
    if (!window.XMLHttpRequest || window.XMLHttpRequest.__cartDrawerPatched) return;

    const OriginalXHR = window.XMLHttpRequest;

    function PatchedXHR() {
      const xhr = new OriginalXHR();
      let watched = false;

      const originalOpen = xhr.open;
      xhr.open = function (method, url, ...rest) {
        watched = !internalCartFetchInFlight && isCartMutationUrl(url);
        return originalOpen.call(this, method, url, ...rest);
      };

      xhr.addEventListener('loadend', () => {
        if (watched) refreshFromServer();
      });

      return xhr;
    }

    PatchedXHR.__cartDrawerPatched = true;
    window.XMLHttpRequest = PatchedXHR;
    window.XMLHttpRequest.__cartDrawerPatched = true;
  }

  function ensureInitialized() {
    if (initialized) return;
    initialized = true;

    patchFetch();
    patchXHR();

    // Initial sync (in case drawer is opened with existing cart)
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          refreshFromServer();
        },
        { once: true }
      );
    } else {
      refreshFromServer();
    }
  }

  return {
    ensureInitialized,
    refreshFromServer,
  };
})();

// Keep it available (optional, but helpful for debugging in console)
window.CartDrawerShippingSync = CartDrawerShippingSync;
